import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';

import {
  IMap,
  Action,
  IRuntimeError,
  TriggerAction,
  TelemetryEvent,
  Error as TelemetryError,
} from '@rccl/telemetry-events';
import { TelemetryContextService } from '@rccl/telemetry-angular';

import { TelemetryGenericEnum } from '../enums';
import { errorCodesList, unknownErrorCode } from '../constants';
import { ITelemetryContextError, ITelemetryErrorCode } from '../interfaces';
import {
  EndPointsErrorLookup,
  GraphQLErrorLookup,
} from '../types/endpoints-error-codes.type';

@Injectable({
  providedIn: 'root',
})
export class SplunkService {
  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly telemetryContext: TelemetryContextService
  ) {
    if (this.telemetryContext?.telemetryConfig?.config?.appVers) {
      this.telemetryContext.telemetryConfig.config.appVers =
        this.document?.documentElement?.dataset['branch'] ?? '1.0.0';
    }
  }

  public initializeContext(
    label: string[] | string,
    value: any
  ): IMap<string | number> {
    const context: IMap<string | number> = {};
    const labels = [].concat(label as never[]);
    const values = [].concat(value);

    for (let k = 0; k < labels.length; k++) {
      context[labels[k]] = values[k];
    }
    return context;
  }

  public getTelemetryErrorObject(
    err: any,
    url = 'Unknown',
    body = null
  ): ITelemetryContextError {
    const { statusMessage } = err;
    const formattedError = this.formatError(err);
    if (statusMessage) {
      return this.buildInitialContext(statusMessage, url, body);
    }

    const { developerMessage, internalMessage, ...rest } =
      formattedError.error?.errors?.[0] || {};

    const context: ITelemetryContextError = {
      ...rest,
      devError: err?.developerMessage || developerMessage || '',
      message: err?.message || internalMessage || '',
      status: err?.status || 'failure',
      statusCode: err?.statusCode || err?.status,
      path: url,
    };

    if (body) {
      context.payload = JSON.stringify(body);
    }

    return context;
  }

  formatError(err: any): any {
    const errors: any[] = [];
    let formattedError = { error: { errors } };
    if (err.error === null || !err.error) {
      formattedError.error.errors.push({
        developerMessage: err.message || 'Something went wrong',
        internalMessage: err.statusText || 'Something went wrong',
        status: err?.status || 600,
        statusCode: err.status || 600,
      });
    } else {
      formattedError = err;
    }
    return formattedError;
  }

  buildInitialContext(
    statusMessage: any,
    url: string,
    body?: any
  ): ITelemetryContextError {
    const { devError, message } = this.retrieveDevError(statusMessage);

    const context: ITelemetryContextError = {
      devError,
      message,
      status: statusMessage?.status || TelemetryGenericEnum.DEFAULT_STATUS_CODE,
      path: url,
    };
    if (body) {
      context.payload = JSON.stringify(body);
    }

    return context;
  }

  retrieveDevError(statusMessage: any) {
    if (statusMessage?.errors && Array.isArray(statusMessage.errors)) {
      return {
        devError:
          statusMessage?.errors[0]?.errorCode ||
          TelemetryGenericEnum.EMPTY_VALUE,
        message:
          statusMessage?.errors[0]?.internalMessage ||
          TelemetryGenericEnum.EMPTY_VALUE,
      };
    }

    if (statusMessage?.error) {
      return {
        devError: TelemetryGenericEnum.UNKNOWN_BACKEND_ERROR,
        message: statusMessage?.error,
      };
    }

    return {
      devError: TelemetryGenericEnum.UNKNOWN_BACKEND_ERROR,
      message: TelemetryGenericEnum.NO_VALUE_ERROR_MSG,
    };
  }

  public errorByLookUp(
    lookup: EndPointsErrorLookup | GraphQLErrorLookup,
    runtimeError?: IRuntimeError
  ): TelemetryError | undefined {
    const event = this.getTelemetryError(lookup, runtimeError);
    this.dispatchEvent(event, TriggerAction.Error);
    return event;
  }

  public getTelemetryError(
    lookup: EndPointsErrorLookup | GraphQLErrorLookup,
    runtimeError?: IRuntimeError
  ): TelemetryError | undefined {
    const error: ITelemetryErrorCode = this.findRuntimeError(lookup);

    if (!error) return;

    return new TelemetryError({
      tag: error.tag,
      errId: error.code,
      group: error.group,
      errMsg: error.label,
      stack: (runtimeError && runtimeError.stack) ?? undefined,
      context: runtimeError && runtimeError.context,
    });
  }

  private dispatchEvent(
    telemetryEvent: TelemetryEvent | undefined,
    telemetryAction: TriggerAction
  ): void {
    const action = new Action({
      data: telemetryEvent,
      subType: telemetryAction,
      actionType: 200,
    });
    this.telemetryContext.dispatch.emit(action);
  }

  private findRuntimeError(
    lookup: EndPointsErrorLookup | GraphQLErrorLookup
  ): ITelemetryErrorCode {
    const error = errorCodesList.find((err) => err.lookup === lookup);
    return error ?? unknownErrorCode;
  }
}
